import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ErrorReporter from './components/support/ErrorReporter';
import HomePage from './pages/HomePage';

// Lazy load heavy pages for better performance
const ReportPage = lazy(() => import('./pages/ReportPage'));
const MarketIntelligenceReportPage = lazy(() => import('./pages/MarketIntelligenceReportPage'));
const DiscoveryQuestionsPage = lazy(() => import('./pages/DiscoveryQuestionsPage'));
const EnhancedMarketIntelligencePage = lazy(() => import('./pages/EnhancedMarketIntelligencePage'));
const SmartMatchPage = lazy(() => import('./pages/SmartMatchPage'));
const SourcingStudioPage = lazy(() => import('./pages/SourcingStudioPage'));
const SmartMatchReportPage = lazy(() => import('./pages/SmartMatchReportPage'));
const SmartSourcingSimplified = lazy(() => import('./pages/SmartSourcingSimplified'));
const AdvancedSourcingPage = lazy(() => import('./pages/AdvancedSourcingPageNew'));
const TAGlobalJobsPage = lazy(() => import('./pages/TAGlobalJobsPage'));
const AdminSeedPage = lazy(() => import('./pages/AdminSeedPage'));
const AdminBlogManagement = lazy(() => import('./pages/AdminBlogManagement'));
const AdminPresentationPage = lazy(() => import('./pages/AdminPresentationPage'));
const AdminTokenAnalyticsDashboard = lazy(() => import('./pages/AdminTokenAnalyticsDashboard'));
const AdminLLMConfiguration = lazy(() => import('./pages/AdminLLMConfiguration'));
const AdminBlogSeeding = lazy(() => import('./pages/AdminBlogSeeding'));
const SourcingHelpPage = lazy(() => import('./pages/SourcingHelpPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const LatestNewsPage = lazy(() => import('./pages/LatestNewsPage'));
const LatestTrendsPage = lazy(() => import('./pages/LatestTrendsPage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const CertificationsPage = lazy(() => import('./pages/CertificationsPage'));
const MyCertificationsPage = lazy(() => import('./pages/MyCertificationsPage'));
const CoursePage = lazy(() => import('./pages/CoursePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsConditionsPage = lazy(() => import('./pages/TermsConditionsPage'));
const AIStatementPage = lazy(() => import('./pages/AIStatementPage'));
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancelled = lazy(() => import('./pages/PaymentCancelled'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const SuperAdminDashboardNew = lazy(() => import('./pages/SuperAdminDashboardNew'));
const EnhancedOjasPage = lazy(() => import('./pages/EnhancedOjasPage'));
const ExamPage = lazy(() => import('./pages/ExamPage'));
const ExamResultPage = lazy(() => import('./pages/ExamResultPage'));
const RecommendationStatusPage = lazy(() => import('./pages/RecommendationStatusPage'));
import EnhancedRecommendationSubmitPage from './pages/EnhancedRecommendationSubmitPage';
import CertificateViewPage from './pages/CertificateViewPage';
import CertificateVerificationPage from './pages/CertificateVerificationPage';
import PostJobPage from './pages/PostJobPage';
import MyJobsPage from './pages/MyJobsPage';
import JobBoardPage from './pages/JobBoardPage';
import JobDetailPage from './pages/JobDetailPage';
import ApplicantsPage from './pages/ApplicantsPage';
import JobAnalyticsPage from './pages/JobAnalyticsPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import EnhancedProfilePage from './pages/EnhancedProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import AwardsLandingPage from './pages/AwardsLandingPage';
import NominationFormPage from './pages/NominationFormPage';
import NomineeProfilePage from './pages/NomineeProfilePage';
import NomineeDashboardPage from './pages/NomineeDashboardPage';
import AwardsWinnersPage from './pages/AwardsWinnersPage';
import AdminAwardsPage from './pages/AdminAwardsPage';
import AdminAwardsManagement from './pages/AdminAwardsManagement';
import MyActivityPage from './pages/MyActivityPage';
import JobApplicantsPage from './pages/JobApplicantsPage';
import AwardsProgressPage from './pages/AwardsProgressPage';
import BlogLandingPage from './pages/BlogLandingPage';
import BlogEditorPage from './pages/BlogEditorPage';
import BlogPostPage from './pages/BlogPostPage';
import OjasChat from './components/OjasChat';
import { WelcomeSplash } from './components/WelcomeSplash';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
const EmployerPortalPage = lazy(() => import('./pages/EmployerPortalPage'));
const EmployerDashboardPage = lazy(() => import('./pages/EmployerDashboardPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const RecruiterSitePage = lazy(() => import('./pages/RecruiterSitePage'));
const ReferralsPage = lazy(() => import('./pages/ReferralsPage'));
const MatchedJobsPage = lazy(() => import('./pages/MatchedJobsPage'));
const AdminMarketplacePanel = lazy(() => import('./pages/AdminMarketplacePanel'));
const CommunityFeedPage = lazy(() => import('./pages/CommunityFeedPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const ExtensionPage = lazy(() => import('./pages/ExtensionPage'));
const JobProjectsPage = lazy(() => import('./pages/JobProjectsPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <ThemeProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/news" element={<LatestNewsPage />} />
            <Route path="/trends" element={<LatestTrendsPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/blog" element={<BlogLandingPage />} />
            <Route path="/blog/editor" element={<BlogEditorPage />} />
            <Route path="/blog/editor/:slug" element={<BlogEditorPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/certifications" element={<CertificationsPage />} />
            <Route path="/my-certifications" element={<MyCertificationsPage />} />
            <Route path="/my-activity" element={<MyActivityPage />} />
            <Route path="/job-applicants" element={<JobApplicantsPage />} />
            <Route path="/awards-progress" element={<AwardsProgressPage />} />
            <Route path="/certifications/:certificationLevel/course" element={<CoursePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-conditions" element={<TermsConditionsPage />} />
            <Route path="/ai-statement" element={<AIStatementPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/profile" element={<EnhancedProfilePage />} />
            <Route path="/profile/:userId/public" element={<PublicProfilePage />} />
            <Route path="/profile/:userId" element={<PublicProfilePage />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<SuperAdminDashboardNew />} />
            <Route path="/admin/seed" element={<AdminSeedPage />} />
            <Route path="/market-intelligence" element={<EnhancedMarketIntelligencePage />} />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/mi-report/:reportId" element={<MarketIntelligenceReportPage />} />
            <Route path="/discovery/:jobId" element={<DiscoveryQuestionsPage />} />
            <Route path="/sourcing-studio" element={<SourcingStudioPage />} />
            <Route path="/profile/360" element={<Navigate to="/profile" replace />} />
            <Route path="/employers" element={<EmployerPortalPage />} />
            <Route path="/employer/dashboard" element={<EmployerDashboardPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/r/:handle" element={<RecruiterSitePage />} />
            <Route path="/referrals" element={<ReferralsPage />} />
            <Route path="/matched-jobs" element={<MatchedJobsPage />} />
            <Route path="/admin/marketplace" element={<AdminMarketplacePanel />} />
            <Route path="/community" element={<CommunityFeedPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/extension" element={<ExtensionPage />} />
            <Route path="/job-projects" element={<JobProjectsPage />} />
            <Route path="/smartmatch" element={<SmartMatchPage />} />
          <Route path="/smartmatch/report/:sessionId" element={<SmartMatchReportPage />} />
            <Route path="/smart-sourcing" element={<SmartSourcingSimplified />} />
            <Route path="/advanced-sourcing" element={<AdvancedSourcingPage />} />
            <Route path="/ta-global-jobs" element={<TAGlobalJobsPage />} />
            <Route path="/smartmatch/results" element={<SmartMatchPage />} />
            <Route path="/sourcing-help" element={<SourcingHelpPage />} />
            <Route path="/ojas" element={<EnhancedOjasPage />} />
          <Route path="/exam" element={<ExamPage />} />
          <Route path="/certifications/:certificationLevel/exam" element={<ExamPage />} />
          <Route path="/exam/result/:sessionId" element={<ExamResultPage />} />
          <Route path="/recommendations/status" element={<RecommendationStatusPage />} />
          <Route path="/recommend/:token" element={<EnhancedRecommendationSubmitPage />} />
          <Route path="/certificate/:certificateId" element={<CertificateViewPage />} />
          <Route path="/verify/:certificateId" element={<CertificateVerificationPage />} />
          <Route path="/post-job" element={<PostJobPage />} />
          <Route path="/post-job/:jobId" element={<PostJobPage />} />
          <Route path="/my-jobs" element={<MyJobsPage />} />
          <Route path="/jobs" element={<JobBoardPage />} />
          <Route path="/jobs/:jobId" element={<JobDetailPage />} />
          <Route path="/jobs/:jobId/applicants" element={<ApplicantsPage />} />
          <Route path="/jobs/:jobId/analytics" element={<JobAnalyticsPage />} />
            {/* Awards Routes */}
            <Route path="/awards" element={<AwardsLandingPage />} />
            <Route path="/awards/nominate" element={<NominationFormPage />} />
            <Route path="/awards/nominee/:uniqueId" element={<NomineeProfilePage />} />
            <Route path="/awards/my-dashboard" element={<NomineeDashboardPage />} />
            <Route path="/awards/winners/:year" element={<AwardsWinnersPage />} />
            <Route path="/admin/awards" element={<AdminAwardsPage />} />
            <Route path="/admin/awards-management" element={<AdminAwardsManagement />} />
            <Route path="/admin/blog" element={<AdminBlogManagement />} />
            <Route path="/admin/blog-seeding" element={<AdminBlogSeeding />} />
            <Route path="/admin/presentation" element={<AdminPresentationPage />} />
            <Route path="/admin/token-analytics" element={<AdminTokenAnalyticsDashboard />} />
            <Route path="/admin/llm-config" element={<AdminLLMConfiguration />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
            <Route
              path="*"
              element={
                <div data-testid="not-found-page" className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center px-6 text-center">
                  <p className="text-6xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">404</p>
                  <h1 className="text-2xl font-semibold mb-2">Page not found</h1>
                  <p className="text-slate-400 mb-8">This page may have moved. Try the homepage or Sourcing Studio.</p>
                  <div className="flex gap-4">
                    <a href="/" className="px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 font-semibold text-sm">Go Home</a>
                    <a href="/sourcing-studio" className="px-6 py-3 rounded-lg border border-slate-700 font-semibold text-sm hover:bg-slate-800">Sourcing Studio</a>
                  </div>
                </div>
              }
            />
              </Routes>
            </Suspense>
            <OjasChat />
            <WelcomeSplash />
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster />
        <SonnerToaster position="bottom-right" richColors />
        <ErrorReporter />
      </div>
  );
}

export default App;